import {
  resolveTerminalParams,
  TERMINAL_PARAM_GLOBAL_SCOPE_ID,
  type TerminalParamAssignment as ContractAssignment,
  type TerminalParamDescriptor,
  type TerminalParamScope,
  type TerminalParamValue,
} from '@reform-lite/contracts';
import type { TerminalParamAssignment } from '@/hooks/use-terminal-params';
import type { EditorTarget } from './editor-target';

/**
 * Which scope won, and what clearing would uncover (RL-274, TP-FR-016;
 * RL-275, TP-FR-017).
 *
 * Resolution runs through the contract's `resolveTerminalParams` — the same
 * function the sync feed uses — over the rows that belong to the target's
 * scope chain. The editor never reimplements precedence; it only asks.
 *
 * `effective` is what the target resolves today. `beneath` is what it would
 * resolve if the row at this scope were cleared: the answer to "and then
 * what does the till use?", which is the most likely misunderstanding of the
 * scope model and is answered on the control rather than in a manual.
 */

export type ValueSource = TerminalParamScope | 'default';

/**
 * Whether the server would deliver this row (TP-FR-026). The editor resolves
 * over delivered rows only, so an orphan the resolver drops is never shown
 * as "set here" — it is listed for attention instead, and saving over it
 * heals the row.
 */
export function delivered(row: TerminalParamAssignment): boolean {
  return row.undelivered === undefined || row.undelivered === null;
}

export type ResolvedValue = { value: TerminalParamValue; source: ValueSource };

export type RowResolution = {
  /** The row authored at exactly this scope, if any. */
  setHere: TerminalParamAssignment | undefined;
  /** What the target resolves today. */
  effective: ResolvedValue;
  /** What the target would resolve with this scope's row cleared. */
  beneath: ResolvedValue;
};

function scopeIdsFor(target: EditorTarget): Record<TerminalParamScope, string | undefined> {
  switch (target.scope) {
    case 'global':
      return { global: TERMINAL_PARAM_GLOBAL_SCOPE_ID, profile: undefined, terminal: undefined };
    case 'profile':
      return {
        global: TERMINAL_PARAM_GLOBAL_SCOPE_ID,
        profile: target.profileId,
        terminal: undefined,
      };
    case 'terminal':
      return {
        global: TERMINAL_PARAM_GLOBAL_SCOPE_ID,
        profile: target.profileId ?? undefined,
        terminal: target.terminalId,
      };
  }
}

function winnerFor(
  descriptor: TerminalParamDescriptor,
  rows: readonly TerminalParamAssignment[],
  scopeIds: Record<TerminalParamScope, string | undefined>,
): ResolvedValue {
  const candidates: ContractAssignment[] = rows
    .filter((row) => row.key === descriptor.key && delivered(row))
    .map((row) => ({
      key: row.key,
      type: row.type as ContractAssignment['type'],
      value: row.value,
      scope: row.scope,
      scopeId: row.scopeId,
    }));
  const [winner] = resolveTerminalParams(candidates, scopeIds);
  return winner === undefined
    ? { value: descriptor.defaultValue, source: 'default' }
    : { value: winner.value, source: winner.sourceScope };
}

export function resolveRow(
  descriptor: TerminalParamDescriptor,
  rows: readonly TerminalParamAssignment[],
  target: EditorTarget,
): RowResolution {
  const scopeIds = scopeIdsFor(target);
  const hereId = scopeIds[target.scope];
  const setHere = rows.find(
    (row) =>
      row.key === descriptor.key &&
      row.scope === target.scope &&
      row.scopeId === hereId &&
      delivered(row),
  );
  const effective = winnerFor(descriptor, rows, scopeIds);
  const beneath =
    setHere === undefined
      ? effective
      : winnerFor(descriptor, rows, { ...scopeIds, [target.scope]: undefined });
  return { setHere, effective, beneath };
}

/**
 * The words `sourceLabel` names a value source with. Passing them in keeps the
 * helper pure: `useT` is a hook and cannot be called from here, so the editor
 * resolves the catalogue rows and hands the results down.
 */
export type SourceWords = {
  registryDefault: string;
  globalValue: string;
  profilePrefix: string;
  profileValue: string;
  terminalPrefix: string;
  terminalValue: string;
};

export const SOURCE_WORDS_EN: SourceWords = {
  registryDefault: 'the registry default',
  globalValue: 'the global value',
  profilePrefix: 'profile',
  profileValue: 'the profile value',
  terminalPrefix: 'terminal',
  terminalValue: 'the terminal value',
};

/** Translator shape, so this module never has to import the `useT` hook. */
export type Translate = (key: string, bakedEnglish?: string) => string;

/** Resolve the source words for the operator's locale. Keys are literal here so
 * the parity guard can see them; the second argument is the baked English. */
export function sourceWords(t: Translate): SourceWords {
  return {
    registryDefault: t('parameters.vocab.sourceRegistryDefault', 'the registry default'),
    globalValue: t('parameters.vocab.sourceGlobalValue', 'the global value'),
    profilePrefix: t('parameters.vocab.sourceProfilePrefix', 'profile'),
    profileValue: t('parameters.vocab.sourceProfileValue', 'the profile value'),
    terminalPrefix: t('parameters.vocab.sourceTerminalPrefix', 'terminal'),
    terminalValue: t('parameters.vocab.sourceTerminalValue', 'the terminal value'),
  };
}

/**
 * Operator-facing name of a value source, in the terminal-config editor's words.
 * `words` defaults to the English the editor has always read, so a caller that
 * passes nothing gets exactly the previous sentence.
 */
export function sourceLabel(
  source: ValueSource,
  target: EditorTarget,
  words: SourceWords = SOURCE_WORDS_EN,
): string {
  switch (source) {
    case 'default':
      return words.registryDefault;
    case 'global':
      return words.globalValue;
    case 'profile':
      return target.scope === 'terminal' && target.profileName !== null
        ? `${words.profilePrefix} ${target.profileName}`
        : words.profileValue;
    case 'terminal':
      return target.scope === 'terminal'
        ? `${words.terminalPrefix} ${target.terminalLabel}`
        : words.terminalValue;
  }
}

/**
 * What clearing at this scope does, in one sentence (TP-FR-017). Clearing
 * uncovers the value beneath — it never sets false or empty — and the
 * control names that value so the operator reads the outcome, not the
 * mechanism: "Clears this profile value. Terminals on this profile will use
 * the global value: true".
 */
export type ClearWords = SourceWords & {
  clearsGlobal: string;
  clearsProfile: string;
  clearsTerminal: string;
  /**
   * Complete sentences for a `whenUnset: 'keepCurrent'` key with nothing set
   * beneath (RL-423): clearing uncovers no value, so the till leaves the
   * device as it is and nothing is switched back.
   */
  clearsGlobalKeepCurrent: string;
  clearsProfileKeepCurrent: string;
  clearsTerminalKeepCurrent: string;
};

/**
 * The three lead-ins each end without punctuation: the uncovered value is
 * appended after a space.
 */
export const CLEAR_WORDS_EN: ClearWords = {
  ...SOURCE_WORDS_EN,
  clearsGlobal: 'Clears the global value. Every till without a profile or terminal value will use',
  clearsProfile: 'Clears this profile value. Terminals on this profile will use',
  clearsTerminal: 'Clears this terminal value. The till will use',
  clearsGlobalKeepCurrent:
    'Clears the global value. Tills without a profile or terminal value keep their current setting; nothing is switched back. To change it, set the value instead of clearing it.',
  clearsProfileKeepCurrent:
    'Clears this profile value. Terminals on this profile without their own value keep their current setting; nothing is switched back. To change it, set the value instead of clearing it.',
  clearsTerminalKeepCurrent:
    'Clears this terminal value. The till keeps its current setting; nothing is switched back. To change it, set the value instead of clearing it.',
};

/** Resolve the clear-outcome words for the operator's locale. */
export function clearWords(t: Translate): ClearWords {
  return {
    ...sourceWords(t),
    clearsGlobal: t(
      'parameters.vocab.clearsGlobal',
      'Clears the global value. Every till without a profile or terminal value will use',
    ),
    clearsProfile: t(
      'parameters.vocab.clearsProfile',
      'Clears this profile value. Terminals on this profile will use',
    ),
    clearsTerminal: t(
      'parameters.vocab.clearsTerminal',
      'Clears this terminal value. The till will use',
    ),
    clearsGlobalKeepCurrent: t(
      'parameters.vocab.clearsGlobalKeepCurrent',
      'Clears the global value. Tills without a profile or terminal value keep their current setting; nothing is switched back. To change it, set the value instead of clearing it.',
    ),
    clearsProfileKeepCurrent: t(
      'parameters.vocab.clearsProfileKeepCurrent',
      'Clears this profile value. Terminals on this profile without their own value keep their current setting; nothing is switched back. To change it, set the value instead of clearing it.',
    ),
    clearsTerminalKeepCurrent: t(
      'parameters.vocab.clearsTerminalKeepCurrent',
      'Clears this terminal value. The till keeps its current setting; nothing is switched back. To change it, set the value instead of clearing it.',
    ),
  };
}

/**
 * Whether the target has no value at any scope for a key whose till then
 * leaves the device as it is (RL-423, `whenUnset: 'keepCurrent'`). The
 * registry default is then not the value in effect and must not be shown as
 * one.
 */
export function keepsCurrent(
  descriptor: Pick<TerminalParamDescriptor, 'whenUnset'>,
  resolved: ResolvedValue,
): boolean {
  return descriptor.whenUnset === 'keepCurrent' && resolved.source === 'default';
}

export function clearOutcome(
  formatted: (value: TerminalParamValue) => string,
  target: EditorTarget,
  resolution: RowResolution,
  words: ClearWords = CLEAR_WORDS_EN,
  descriptor: Pick<TerminalParamDescriptor, 'whenUnset'> = {},
): string {
  const { beneath } = resolution;
  if (keepsCurrent(descriptor, beneath)) {
    switch (target.scope) {
      case 'global':
        return words.clearsGlobalKeepCurrent;
      case 'profile':
        return words.clearsProfileKeepCurrent;
      case 'terminal':
        return words.clearsTerminalKeepCurrent;
    }
  }
  const uses = `${sourceLabel(beneath.source, target, words)}: ${formatted(beneath.value)}`;
  switch (target.scope) {
    case 'global':
      return `${words.clearsGlobal} ${uses}`;
    case 'profile':
      return `${words.clearsProfile} ${uses}`;
    case 'terminal':
      return `${words.clearsTerminal} ${uses}`;
  }
}
